"use client";

import { useEffect, useRef, useState, useCallback } from 'react';
import { WorkspaceWebSocket } from '@/lib/ws';
import { WS_URL } from '@/lib/constants';

/**
 * Decode the accountId from the locally-stored JWT.
 *
 * Workspace-main on Render needs `account_id` to write conversations and
 * messages, and it's supposed to extract this from the JWT in the WS upgrade
 * URL — but on 2026-05-02 we observed `conversations.account_id is required`
 * SQL errors, suggesting the server isn't successfully verifying the token
 * (likely a `JWT_SECRET` drift between local-issuer and Render-verifier).
 * Sending `accountId` explicitly in WS frames lets the server bypass token
 * extraction and just trust what the client says. The fallback only matters
 * until JWT_SECRET parity is restored on Render.
 */
function readAccountIdFromToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const token = window.localStorage.getItem('ctrl-a-token');
  if (!token) return undefined;
  const parts = token.split('.');
  if (parts.length !== 3) return undefined;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const accountId = payload?.accountId ?? payload?.account_id ?? undefined;
    return typeof accountId === 'string' ? accountId : undefined;
  } catch {
    return undefined;
  }
}

export function useWebSocket() {
  const wsRef = useRef<WorkspaceWebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [agents, setAgents] = useState<any[]>([]);

  useEffect(() => {
    // Trim because Vercel env var values that come from the dashboard UI
    // sometimes pick up a stray leading space — observed 2026-05-02 when
    // every WS upgrade failed silently with a `%20` prefix on apiKey,
    // sending the client into an infinite reconnect loop. Defensive trim
    // here means the bug class can't recur even if the env value drifts.
    const appKey = (process.env.NEXT_PUBLIC_APP_KEY || '').trim();
    const ws = new WorkspaceWebSocket(WS_URL, appKey);
    wsRef.current = ws;

    const offInit = ws.on('init', (data) => {
      setConnected(true);
      setAgents(data.agents || []);
    });

    const offClose = ws.on('close', () => setConnected(false));
    const offReconnected = ws.on('reconnected', () => setConnected(true));

    ws.connect();

    return () => {
      offInit();
      offClose();
      offReconnected();
      ws.disconnect();
    };
  }, []);

  const sendMessage = useCallback((message: string, conversationId?: string, meta?: { pageContext?: string }) => {
    const accountId = readAccountIdFromToken();
    wsRef.current?.sendMessage(message, conversationId, accountId, meta);
  }, []);

  const selectAgent = useCallback((agentId: string, conversationId?: string) => {
    const accountId = readAccountIdFromToken();
    wsRef.current?.selectAgent(agentId, conversationId, accountId);
  }, []);

  // Long-turn handoff controls (spec 2026-04-20).
  const cancelTurn = useCallback(() => {
    wsRef.current?.cancelTurn();
  }, []);
  const backgroundTurn = useCallback(() => {
    wsRef.current?.backgroundTurn();
  }, []);

  const onResponse = useCallback((handler: (data: any) => void) => {
    return wsRef.current?.on('response', handler) ?? (() => {});
  }, []);

  const onTyping = useCallback((handler: (data: any) => void) => {
    return wsRef.current?.on('typing', handler) ?? (() => {});
  }, []);

  // Subscribes to BOTH `tool_activity` (start) and `tool_result` (end)
  // frames from base-agent.ts. The chat-context handler keys on `data.done`
  // / `data.error` to flip the activity panel from "working" → "complete" /
  // "error". Without translating `tool_result` into a `done: true` event,
  // the activity panel sticks at WORKING forever (delegate_task stuck-state
  // observed 2026-05-04). `tool_result.isError` maps to the handler's
  // `error` field.
  const onToolActivity = useCallback((handler: (data: any) => void) => {
    const offActivity = wsRef.current?.on('tool_activity', handler) ?? (() => {});
    const offResult =
      wsRef.current?.on('tool_result', (data: any) =>
        handler({ ...data, done: true, error: data?.isError === true }),
      ) ?? (() => {});
    return () => {
      offActivity();
      offResult();
    };
  }, []);

  return {
    connected,
    agents,
    sendMessage,
    selectAgent,
    cancelTurn,
    backgroundTurn,
    onResponse,
    onTyping,
    onToolActivity,
    ws: wsRef.current,
  };
}
